import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CollapsibleTrigger } from "@/components/ui/collapsible";
import { PersonaAvatar } from "@/components/persona-identity";
import { CanEdit } from "@/lib/project-access";
import { WorkRequestChip } from "@/components/experience/work-request-chip";
import { cn } from "@/lib/utils";
import type { OpenWorkRequestRow } from "@iamu/db";
import { RunDialog } from "./panel-run-dialog";

interface PanelHeaderProps {
  persona: { id: string; name: string; role: string | null } | null;
  stateLine: React.ReactNode;
  scenarioRequest?: OpenWorkRequestRow | null;
  onCancelRequest?: () => void;
  personaName: string;
  scenarioId: string | null;
  projectId: string;
  open: boolean;
}

export function PanelHeader({
  persona,
  stateLine,
  scenarioRequest,
  onCancelRequest,
  personaName,
  scenarioId,
  projectId,
  open,
}: PanelHeaderProps) {
  const [showRun, setShowRun] = useState(false);

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      {/* Named by its visible content (persona, role, state); the
          collapsible supplies aria-expanded. */}
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex min-w-[240px] flex-1 items-start gap-4 text-left cursor-pointer"
        >
          <PersonaAvatar name={personaName} size="md" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-md font-semibold text-foreground">
                {personaName}
              </span>
              {persona?.role && (
                <span className="text-sm text-muted-foreground">
                  · {persona.role}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div>{stateLine}</div>
              {scenarioRequest && (
                <WorkRequestChip
                  request={scenarioRequest}
                  onCancel={onCancelRequest}
                />
              )}
            </div>
          </div>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      </CollapsibleTrigger>

      <div className="flex items-center gap-2 shrink-0 self-start">
        {persona?.id && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-9 text-xs text-muted-foreground hover:text-foreground"
          >
            <Link to={`/projects/${projectId}/personas/${persona.id}`}>
              Persona profile →
            </Link>
          </Button>
        )}
        <CanEdit>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRun(true)}
            disabled={!scenarioId}
          >
            <Play className="mr-2 h-3.5 w-3.5" />
            Run as {personaName.split(" ")[0]}
          </Button>

          {scenarioId && (
            <RunDialog
              open={showRun}
              onOpenChange={setShowRun}
              scenarioId={scenarioId}
              projectId={projectId}
            />
          )}
        </CanEdit>
      </div>
    </div>
  );
}
